import * as React from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Pricelist from './Pricelist';
import authService from './api-authorization/AuthorizeService';
import { CustomerPriceListItem } from './models/CustomerInterfaces';
import { GenericList } from './models/JobSharedInterfaces';

interface CustomerPricingProps {
    id: number;
    readOnly: boolean;
    priceListTierId: number;
    fencePartPickupFee: number;
    calloutFee: number;
    fuelSurcharge: number;
    fuelSurchargePumpClean: number;
    customPriceList: CustomerPriceListItem[];
    hasCustomPriceList: boolean;
    scaffoldQuoteNotes: string;
    dunnyQuoteNotes: string;
    errors: { [key: string]: string };
    updatePriceListTierId(fieldName: string, fieldValue: number): void;
    updateCustom(customPriceList: CustomerPriceListItem[]): void;
    updateQuoteNotes(scaffoldQuoteNotes: string, dunnyQuoteNotes: string): void;
    updateErrors(errors: { [key: string]: string }): void;
}

interface Item {
    id: number;
    name: string;
    defaultDays: number;
    typeId: number;
    overhireRequired: boolean;
}

const CustomerPricelist = (props: CustomerPricingProps) => {
    const [systemCompanyId, setSystemCompanyId] = React.useState(2);
    const [items, setItems] = React.useState<Item[]>([]);
    const [priceListTiers, setPriceListTiers] = React.useState<GenericList[]>([]);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        window.scrollTo(0, 0);

        getData();
    }, [props.id]);

    const getData = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        axios.get('api/Customer/GetCustomerPricingDropdowns?SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            setSystemCompanyId(res.data.systemCompanyId);
            setItems(res.data.items);
            setPriceListTiers(res.data.priceListTiers);
            setLoading(false);
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const updateError = (errorName: string) => {
        var error = props.errors;
        delete error[errorName];
        props.updateErrors(error);
    }

    const handleChange = (e: any, type: number) => {
        //check if there is a value and an error, and if so remove from error list
        updateError(e.target.name);

        let quoteNotes = e.target.value;
        let scaffold = props.scaffoldQuoteNotes;
        let dunny = props.dunnyQuoteNotes;

        if (type === 1) {
            scaffold = quoteNotes;
        } else {
            dunny = quoteNotes;
        }

        props.updateQuoteNotes(scaffold, dunny);
    }

    const handlePriceListTierChange = (e: any) => {
        let tierId = e.target.value * 1;
        props.updatePriceListTierId(e.target.name, tierId);
    }

    const handlePriceListCalloutFeeChange = (e: any) => {
        let callout = e.target.value * 1;
        props.updatePriceListTierId(e.target.name, callout);
    }

    const handlePriceListFencePartPickupFeeChange = (e: any) => {
        let fencePartPickup = e.target.value * 1;
        props.updatePriceListTierId(e.target.name, fencePartPickup);
    }

    const handleFuelSurchargeChange = (e: any) => {
        let fuelSurcharge = e.target.value * 1;
        props.updatePriceListTierId(e.target.name, fuelSurcharge);
    }

    const updatePricelist = (customPriceList: CustomerPriceListItem[]) => {
        props.updateCustom(customPriceList);
    }

    let pricelist = <Pricelist systemCompanyId={systemCompanyId} id={props.id} priceListTierTypeId={2} pricelist={props.customPriceList} itemList={items} customerOptions={[]} frequencyList={[]} locationList={[]} metresList={[]} isEpa={false} isPumpClean={false} isFencing={false} isFencingCustom={false} isEvent={false} isPriceOnly={false} update={updatePricelist} />
    
    const renderDetails = (
        <div>
            <div>
                <div>
                    <label className={systemCompanyId === 2 ? "input-group" : "hidden"} htmlFor="scaffoldQuoteNotes">
                        <span className="label label-large">Scaffold Quote Notes</span>
                        <textarea className="input job__noteSize marginBottom10" id="scaffoldQuoteNotes" name="scaffoldQuoteNotes" value={props.scaffoldQuoteNotes} onChange={(e) => handleChange(e, 1)}></textarea>
                    </label>

                    <label className={systemCompanyId === 3 ? "input-group" : "hidden"} htmlFor="dunnyQuoteNotes">
                        <span className="label label-large">Dunny Pricing Notes</span>
                        <textarea className="input job__noteSize marginBottom10" id="dunnyQuoteNotes" name="dunnyQuoteNotes" value={props.dunnyQuoteNotes} onChange={(e) => handleChange(e, 2)}></textarea>
                    </label>
                </div>
                <div className={systemCompanyId === 3 ? "" : "hidden"}>
                    <label className="input-group" htmlFor="priceListTierId">
                        <span className="label label-large">Price List Tier</span>
                        <select className="select" id="priceListTierId" name="priceListTierId" value={props.priceListTierId} onChange={(e) => handlePriceListTierChange(e)} disabled={props.readOnly} >
                            <option defaultValue="-1"></option>
                            {priceListTiers.map(tier =>
                                <option key={tier.id} value={tier.id}>{tier.name}</option>
                            )};
                        </select>
                    </label>
                    <label className="input-group" htmlFor="fencePartPickupFee">
                        <span className="label label-large">Fence Part Pickup Fee</span>
                        <input type='number' min="0" step="0.01" className='input input--medium textalignright' id="fencePartPickupFee" name="fencePartPickupFee" value={props.fencePartPickupFee} onChange={(e) => handlePriceListFencePartPickupFeeChange(e)} />
                    </label>
                    <label className="input-group" htmlFor="calloutFee">
                        <span className="label label-large">Callout Fee</span>
                        <input type='number' min="0" step="0.01" className='input input--medium textalignright' id="calloutFee" name="calloutFee" value={props.calloutFee} onChange={(e) => handlePriceListCalloutFeeChange(e)} />
                    </label>
                    <label className="input-group" htmlFor="fuelSurcharge">
                        <span className="label label-large">Fuel Surcharge</span>
                        <input type='number' min="0" step="0.01" className='input input--medium textalignright' id="fuelSurcharge" name="fuelSurcharge" value={props.fuelSurcharge} onChange={(e) => handleFuelSurchargeChange(e)} />
                    </label>
                    <label className="input-group" htmlFor="fuelSurchargePumpClean">
                        <span className="label label-large">Fuel Surcharge P&C</span>
                        <input type='number' min="0" step="0.01" className='input input--medium textalignright' id="fuelSurchargePumpClean" name="fuelSurchargePumpClean" value={props.fuelSurchargePumpClean} onChange={(e) => handleFuelSurchargeChange(e)} />
                    </label>
                </div>
                <div className={props.hasCustomPriceList ? "" : "hidden"}>
                    {pricelist}
                    {props.errors["pricelistitems"] ?
                        (props.errors["pricelistitems"]).split("\n").map((item: any, key: any) => {
                            return <span className="label errors" key={key}>{item}<br /></span>
                        })
                        : ""}
                </div>
            </div>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            {contents}
        </div>
    );

}

export default CustomerPricelist;




